import {isObject} from '@vueuse/core';
import type {Availability, AvailabilityArticle} from '../interfaces/availability.type';
import type {BaseOrderLine, OrderLineWithAvailability, ShoppingCartOrderLine} from '../interfaces/order.line.type';
import type {BaseProduct, PackingUnitPerCompanyId} from '../interfaces/product.type';
import {getAvailabilityByOrderLine} from './availability.service';
import {shoppingCartStorage} from './shoppingcart.storage.service';
import {isInvalidAmount, isInvalidTotalAmountForVariety} from './order.line.validation.service';

/**
 * Get the packing unit for the given product.
 * Returns 100 if the packing unit is unknown.
 *
 * @param packingUnitPerCompanyId The product packing unit per companyId.
 */
export const getPackingUnit = (packingUnitPerCompanyId: PackingUnitPerCompanyId) => {
	if (!packingUnitPerCompanyId) {
		return 100;
	}

	const companyId = window.userCompanyId;

	// Get the correct packingUnit for the companyId.
	// Fallback to the default 100 whenever there is no specific packingUnit for a company.
	return packingUnitPerCompanyId[companyId] ?? 100;
};

/**
 * Convert a Product to a BaseOrderLine with the given amount, week, year and region.
 *
 * @param week Delivery week
 * @param year Delivery year
 * @param region Product region
 * @param product Product to order
 * @param newAmount Amount to order
 * @param allowOverbooking Whether overbooking is allowed for this order line
 */
export const createOrderLine = (
	week: number,
	year: number,
	region: string,
	product: BaseProduct,
	newAmount: number,
	allowOverbooking: boolean
	// eslint-disable-next-line max-params
): BaseOrderLine => {

	return {
		amount: newAmount,
		articleId: product.patcode,
		consignment: null,
		formId: product.form?.patId,
		formName: product.form?.shortName,
		groupId: '',
		groupName: product.productgroup,
		locationId: product.location?.shortName || null,
		image: product.image,
		notExistingVariety: false,
		region,
		preferredDeliveryDay: null,
		packingUnit: getPackingUnit(product.packingUnitPerCompanyId),
		urlFriendlyId: product.urlFriendlyId,
		urlFriendlyName: product.urlFriendlyName,
		urlFriendlyProductgroup: product.urlFriendlyProductgroup,
		articleName: product.name,
		type: product.type,
		week,
		year,
		allowOverbooking
	};
};

export const roundUpAmount = (amount: number, packingUnit: number): number => {
	return Math.ceil(amount / packingUnit) * packingUnit;
};

/**
 * Rounds the given amount up to the nearest multiple of the given packing unit,
 * but never higher than the available amount.
 *
 * @param amount The amount to round up.
 * @param available The available amount.
 * @param packingUnit The packing unit.
 * @param allowOverbooking Whether overbooking is allowed.
 */
export const getRoundedValue = (
	amount: number,
	available: number,
	packingUnit: number,
	allowOverbooking: boolean
): number => {

	const roundedUpAmount = roundUpAmount(amount, packingUnit);

	if (allowOverbooking) {
		// Unlimited availability, just return the rounded up amount.
		return roundedUpAmount;
	}

	if (roundedUpAmount <= available) {
		// The rounded up amount is within the available range, return it.
		return roundedUpAmount;
	}

	const roundUpAvailableAmount = roundUpAmount(available, packingUnit);

	// If the amount is the same, return it or return the next lower multiple of the packing unit.
	return roundUpAvailableAmount === available ? available : (roundUpAvailableAmount - packingUnit);
};

export const orderLineWithState = (orderLine: BaseOrderLine, availabilities: AvailabilityArticle[], amountAvailableFallback?: number) => {
	const availability: Availability | undefined = getAvailabilityByOrderLine(orderLine, availabilities);

	if (!availability) {
		return orderLine as OrderLineWithAvailability;
	}

	let amountAvailable: number = 0;

	if (availability && isObject(availability)) {
		amountAvailable = availability.amount;
	}

	if (amountAvailableFallback && !availability) {
		amountAvailable = amountAvailableFallback;
	}

	const orderLineWithState: OrderLineWithAvailability = {} as OrderLineWithAvailability;

	Object.assign(orderLineWithState, orderLine);

	Object.assign(orderLineWithState, {
		amountAvailable,
		invalidTotalAmountForVariety: isInvalidTotalAmountForVariety(orderLine, shoppingCartStorage.value.orderLines, amountAvailable),
		invalid: isInvalidAmount(orderLine.amount, orderLine.packingUnit, amountAvailable, orderLine.allowOverbooking)
	});

	return orderLineWithState;
};

export const orderLinesWithState = (orderLines: BaseOrderLine[], availabilities: AvailabilityArticle[]): OrderLineWithAvailability[] => {
	if (!orderLines || orderLines.length < 1) {
		return [];
	}

	return orderLines.map((orderLine: BaseOrderLine) => orderLineWithState(orderLine, availabilities));
};

export const compareOrderLineByGroupAndArticleName = (line1: BaseOrderLine, line2: BaseOrderLine) => {
	return line1.groupName.localeCompare(line2.groupName) || line1.articleName.localeCompare(line2.articleName);
};

export const isSameVarietyAndWeek = (orderLine: BaseOrderLine, other: BaseOrderLine) => {
	return orderLine !== undefined && orderLine !== null &&
		other !== undefined && other !== null &&
		orderLine.articleId === other.articleId &&
		orderLine.formId === other.formId &&
		orderLine.week === other.week &&
		orderLine.year === other.year;
};

export const isSameConsignment = (consignment: string | null, otherConsignment: string | null) => {
	return (!consignment && !otherConsignment) || (consignment === otherConsignment);
};

export const isUnrootedCutting = (formId: string): boolean => {
	return ['001', '002', '003', '004', '005', '006', '007', '008', '009'].includes(formId);
};

export const toShoppingCartOrderLine = (orderLine: BaseOrderLine): ShoppingCartOrderLine => {
	return {
		allowOverbooking: orderLine.allowOverbooking,
		amount: orderLine.amount,
		// Will be updated by shopping cart.
		amountAvailable: 0,
		articleId: orderLine.articleId,
		articleName: orderLine.articleName,
		type: orderLine.type,
		consignment: orderLine.consignment,
		formId: orderLine.formId,
		formName: orderLine.formName,
		groupId: orderLine.groupId,
		groupName: orderLine.groupName,
		image: orderLine.image,
		locationId: orderLine.locationId,
		notExistingVariety: orderLine.notExistingVariety,
		packingUnit: orderLine.packingUnit,
		preferredDeliveryDay: null,
		region: orderLine.region,
		urlFriendlyId: orderLine.urlFriendlyId,
		urlFriendlyName: orderLine.urlFriendlyName,
		urlFriendlyProductgroup: orderLine.urlFriendlyProductgroup,
		week: orderLine.week,
		year: orderLine.year,
		alternativeForms: []
	};
};
